(() => {
  'use strict';
  if (window.__ibLayoutV58) return;
  window.__ibLayoutV58 = true;

  let headerElement;
  let headerGroup;
  let resizeObserver;
  let measureFrame = 0;
  let lastHeaderHeight = -1;
  let lastGroupHeight = -1;
  let lastTopRowHeight = -1;

  const writeMeasurement = (name, value, previous) => {
    if (value === previous) return previous;
    document.body.style.setProperty(name, `${value}px`);
    return value;
  };

  const measureHeader = () => {
    measureFrame = 0;
    if (!headerElement?.isConnected || !headerGroup?.isConnected) return;
    const topRow = headerElement.querySelector('.header__row--top');
    const headerHeight = headerElement.offsetHeight;
    const groupHeight = headerGroup.offsetHeight;
    const topRowHeight = topRow?.offsetHeight || 0;

    lastHeaderHeight = writeMeasurement('--header-height', headerHeight, lastHeaderHeight);
    lastGroupHeight = writeMeasurement('--header-group-height', groupHeight, lastGroupHeight);
    if (topRow) lastTopRowHeight = writeMeasurement('--top-row-height', topRowHeight, lastTopRowHeight);
    else if (lastTopRowHeight !== 0) {
      document.body.style.removeProperty('--top-row-height');
      lastTopRowHeight = 0;
    }
  };

  const scheduleMeasurement = () => {
    if (!measureFrame) measureFrame = requestAnimationFrame(measureHeader);
  };

  const updateMenuStyle = () => {
    const header = document.querySelector('#header-component') || headerElement;
    if (!header) return;
    const isTouchDevice = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
    const hasReachedMinimum = Boolean(header.querySelector('overflow-list')?.hasAttribute('minimum-reached'));
    const next = isTouchDevice || hasReachedMinimum ? 'drawer' : 'menu';
    if (header.dataset.menuStyle !== next) header.dataset.menuStyle = next;
  };

  const connectLayoutObservers = () => {
    const nextHeader = document.querySelector('header-component');
    const nextGroup = document.querySelector('#header-group');
    if (!nextHeader || !nextGroup) return;
    const changed = nextHeader !== headerElement || nextGroup !== headerGroup;
    headerElement = nextHeader;
    headerGroup = nextGroup;

    if (changed && 'ResizeObserver' in window) {
      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(() => {
        scheduleMeasurement();
        updateMenuStyle();
      });
      resizeObserver.observe(headerElement);
      resizeObserver.observe(headerGroup);
    }
    scheduleMeasurement();
    updateMenuStyle();
  };

  // R5: Horizon 4.2 puts the disclosure state on a separate button.
  const MENU_LINK_SELECTOR = [
    '#header-group a.menu-list__link',
    '#header-group a.mega-menu__link--parent',
    '#header-group a.menu-drawer__title',
    '#header-group a.menu-drawer__menu-item'
  ].join(',');

  const isMenuGroup = (link) => {
    if (!(link instanceof HTMLAnchorElement) || !link.matches(MENU_LINK_SELECTOR)) return false;
    if (link.matches('.menu-list__link')) {
      const item = link.closest('.menu-list__list-item');
      return Boolean(item?.querySelector(':scope > .menu-list__submenu')) ||
        link.getAttribute('aria-haspopup') === 'true';
    }
    if (link.matches('.mega-menu__link--parent')) return Boolean(link.parentElement?.querySelector(':scope > ul'));
    if (link.matches('.menu-drawer__title')) return Boolean(link.closest('.menu-drawer__submenu'));
    return Boolean(link.parentElement?.querySelector(':scope > ul'));
  };

  const isPlaceholderDestination = (link) => {
    const href = link.getAttribute('href');
    if (href === null) return link.dataset.ibMenuHeading === 'true';
    if (href.trim() === '' || href.trim() === '#') return true;
    try {
      const url = new URL(href, location.href);
      if (url.origin !== location.origin || url.hash) return false;
      const path = url.pathname.replace(/\/+$/, '');
      const root = new URL(window.Shopify?.routes?.root || '/', location.origin).pathname.replace(/\/+$/, '');
      return path === '' || path === root;
    } catch (_) { return false; }
  };

  const normalizeMenuLink = (link) => {
    if (!isMenuGroup(link)) return false;
    if (!isPlaceholderDestination(link)) {
      if (link.dataset.ibMenuHeading === 'true') delete link.dataset.ibMenuHeading;
      return false;
    }
    link.dataset.ibMenuHeading = 'true';
    // An anchor without href is a non-navigating label. Keep its children and native refs.
    if (link.hasAttribute('href')) link.removeAttribute('href');
    link.removeAttribute('aria-current');
    link.classList.remove('menu-list__link--active', 'menu-drawer__menu-item--active');
    return true;
  };

  const EMPTY_SKU = '__ib_no_sku__';
  const NORMALIZE_SELECTOR = [
    'a[target="_blank"]',
    MENU_LINK_SELECTOR,
    `product-card[data-product-sku="${EMPTY_SKU}"]`,
    `.product-card[data-product-sku="${EMPTY_SKU}"]`
  ].join(',');

  const needsNormalization = (root) => root instanceof Element && (
    root.matches(NORMALIZE_SELECTOR) || Boolean(root.querySelector(NORMALIZE_SELECTOR))
  );

  const normalizeAddedContent = (root = document) => {
    const links = [];
    if (root instanceof Element && root.matches('a[target="_blank"]')) links.push(root);
    root.querySelectorAll?.('a[target="_blank"]').forEach((link) => links.push(link));
    links.forEach((link) => {
      const tokens = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
      tokens.add('noopener');
      tokens.add('noreferrer');
      link.setAttribute('rel', [...tokens].join(' '));
    });

    if (root instanceof Element && root.matches(MENU_LINK_SELECTOR)) normalizeMenuLink(root);
    root.querySelectorAll?.(MENU_LINK_SELECTOR).forEach(normalizeMenuLink);

    root.querySelectorAll?.(
      `product-card[data-product-sku="${EMPTY_SKU}"] .ib-card-sku,` +
      `.product-card[data-product-sku="${EMPTY_SKU}"] .ib-card-sku`
    ).forEach((line) => {
      line.hidden = true;
      line.setAttribute('aria-hidden', 'true');
    });
  };

  const start = () => {
    if (!document.head.querySelector('[data-ib-search-card-integrity]')) {
      const style = document.createElement('style');
      style.dataset.ibSearchCardIntegrity = 'true';
      style.textContent =
        `product-card[data-product-sku="${EMPTY_SKU}"] .ib-card-sku,` +
        `.product-card[data-product-sku="${EMPTY_SKU}"] .ib-card-sku{display:none!important}`;
      document.head.append(style);
    }

    connectLayoutObservers();
    normalizeAddedContent();

    document.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a') : null;
      if (link && normalizeMenuLink(link)) event.preventDefault();
    }, true);

    if ('MutationObserver' in window && document.body) {
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes') {
            if (mutation.target instanceof Element && mutation.target.matches(MENU_LINK_SELECTOR)) {
              normalizeMenuLink(mutation.target);
            }
            continue;
          }
          for (const node of mutation.addedNodes) {
            if (needsNormalization(node)) normalizeAddedContent(node);
          }
        }
      }).observe(document.body, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['href']
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  window.addEventListener('pageshow', () => normalizeAddedContent());
  window.addEventListener('orientationchange', connectLayoutObservers, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    connectLayoutObservers();
    normalizeAddedContent(event.target);
  });
  document.fonts?.ready?.then(scheduleMeasurement);
})();
